import { Button } from '@monorepo/shared/componentsV2/Button';
import { DateRangePickerField } from '@monorepo/shared/componentsV2/fields/DateRangePickerField';
import { Loading } from '@monorepo/shared/componentsV2/Loading';
import { BaseModal } from '@monorepo/shared/componentsV2/modals/BaseModal';
import { useLog } from '@monorepo/shared/hooks/logs/useLog';
import { header3 } from '@monorepo/shared/styles/text';
import {
  LogRefResponse,
  NUMBER_OF_LOG_ENTRIES_EXPORT_LIMIT,
  UTCEquivalentOfLocal,
} from 'mapistry-shared';
import React from 'react';
import { ErrorBoundary } from '@monorepo/shared/componentsV2/ErrorBoundary';
import { useFormState } from 'react-final-form';
import styled from 'styled-components';
import { isValid } from 'date-fns';
import { useLogEntryCount } from '../../hooks/useLogEntryCount';
import type { LogEntryExportFormValues } from './LogEntryExportModal';

const ModalDescription = styled.h2`
  ${header3}
  margin: 0 0 1.125rem;
`;

const DateRangePickerContainer = styled.div`
  display: flex;
  margin: 0 0 1rem;
`;

const ExportDetailsContainer = styled.div`
  height: 2rem;
`;

// This file defines two lightweight components such that
// we can use the log's count/invalid states in the parent
// for async error handling
type LogEntryExportModalContentProps = {
  handleSubmit: () => void;
  logId: string;
  onClose: () => void;
  open: boolean;
  organizationId: string;
  projectId: string;
};

type LogEntryExportFormProps = {
  count: number | undefined;
  hasNoRowsToExport: boolean;
  hasTooManyEntriesToExport: boolean;
  isEntryCountLoading: boolean;
  log: LogRefResponse | undefined;
};

export function LogEntryExportForm({
  count,
  hasNoRowsToExport,
  hasTooManyEntriesToExport,
  isEntryCountLoading,
  log,
}: LogEntryExportFormProps) {
  const getExportDetailsDisplay = (): React.ReactNode => {
    if (isEntryCountLoading) return <Loading size="small" />;
    if (hasTooManyEntriesToExport)
      return `There are too many entries in the selected date range (${count?.toLocaleString()}). \
    You can export up to ${NUMBER_OF_LOG_ENTRIES_EXPORT_LIMIT.toLocaleString()} entries at one time.`;
    if (hasNoRowsToExport)
      return 'There are no entries to export in this date range.';
    return `${count?.toLocaleString()} entry rows will be exported.`;
  };

  return (
    <>
      <ModalDescription>
        Configure your export for <b>{log?.name}</b> Log.
      </ModalDescription>
      <DateRangePickerContainer>
        <DateRangePickerField displayOnSingleLine />
      </DateRangePickerContainer>
      <ExportDetailsContainer>
        {getExportDetailsDisplay()}
      </ExportDetailsContainer>
    </>
  );
}

// this middle component is required as the `count` and `isExportDisabled` vars
// both depend on the form state's `values` and `invalid` vars which must be wrapped
// within a <Form>
export function LogEntryExportModalContent({
  handleSubmit,
  logId,
  onClose,
  open,
  organizationId,
  projectId,
}: LogEntryExportModalContentProps) {
  const { values, invalid } = useFormState<LogEntryExportFormValues>({
    subscription: { values: true, invalid: true },
  });

  const { log, isLoading: isLogLoading } = useLog({ logId, organizationId });
  const { count, isLoading: isEntryCountLoading } = useLogEntryCount({
    after:
      values.from && isValid(values.from)
        ? UTCEquivalentOfLocal(values.from)
        : undefined,
    before:
      values.to && isValid(values.to)
        ? UTCEquivalentOfLocal(values.to)
        : undefined,
    logId,
    organizationId,
    projectId,
  });

  const hasTooManyEntriesToExport =
    count != null && count > NUMBER_OF_LOG_ENTRIES_EXPORT_LIMIT;
  const hasNoRowsToExport = count === 0;
  const isExportDisabled =
    invalid ||
    isEntryCountLoading ||
    isLogLoading ||
    hasTooManyEntriesToExport ||
    hasNoRowsToExport;

  return (
    <BaseModal
      buttons={[
        <Button color="primary" key="cancel" onClick={onClose} variant="text">
          Cancel
        </Button>,
        <Button
          color="primary"
          disabled={isExportDisabled}
          key="save"
          onClick={handleSubmit}
          variant="contained"
        >
          Export
        </Button>,
      ]}
      onClose={onClose}
      open={open}
      title="Export Log Data"
    >
      <ErrorBoundary>
        <LogEntryExportForm
          count={count}
          hasNoRowsToExport={hasNoRowsToExport}
          hasTooManyEntriesToExport={hasTooManyEntriesToExport}
          isEntryCountLoading={isEntryCountLoading}
          log={log}
        />
      </ErrorBoundary>
    </BaseModal>
  );
}
